/**
 * Optional native input and autofill aids with prefill tags and inline errors.
 * Choice fields use visible radio options. Sending belongs to the main composer.
 */
import { useId, type FC, type ReactNode } from 'react';
import { cn } from '@/app/lib/utils';
import { Input } from '@/app/lib/shadcdn/input';
import { Textarea } from '@/app/lib/shadcdn/textarea';
import { Label } from '@/app/lib/shadcdn/label';

export interface SmartFieldOption {
  value: string;
  label: string;
}

export interface SmartField {
  id: string;
  label: string;
  kind: 'text' | 'email' | 'phone' | 'choice' | 'note';
  options?: SmartFieldOption[];
  value?: string;
  /** Shows the "· pre-filled" tag. */
  prefilled?: boolean;
  errors?: string[];
  autoComplete?: string;
}

export interface SmartFormFieldsProps {
  fields: SmartField[];
  onChange: (id: string, value: string) => void;
  /** Fired when a field loses focus — the agent gets what was typed without
   *  anything being submitted. Blur rather than keystroke: `<ui_state>` is
   *  re-read on every model call, so per-character syncing is pure waste. */
  onCommit?: () => void;
  /** Maximum columns; narrow containers use one and a lone field spans the row. */
  columns?: 1 | 2;
  prefilledLabel: string;
  disabled?: boolean;
}

const CONTROL_CLASS_NAME =
  'h-10 border-border bg-card px-3 text-sm text-foreground shadow-none focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/15';

/** `kind` → `inputmode`, for kind-appropriate mobile keyboards. */
function inputModeFor(kind: SmartField['kind']): 'tel' | 'email' | 'text' {
  if (kind === 'phone') {
    return 'tel';
  }
  if (kind === 'email') {
    return 'email';
  }
  return 'text';
}

function autoCompleteFor(field: SmartField): string | undefined {
  if (field.autoComplete) return field.autoComplete;
  if (field.kind === 'phone') return 'tel';
  if (field.kind === 'email') return 'email';
  return undefined;
}

function renderControl(
  field: SmartField,
  onChange: (id: string, value: string) => void,
  onCommit?: () => void,
  disabled?: boolean,
  controlId = field.id,
): ReactNode {
  if (field.kind === 'choice') {
    const options = field.options ?? [];
    return (
      <div
        id={controlId}
        role="radiogroup"
        aria-label={field.label}
        className="flex flex-wrap gap-4"
      >
        {options.map((option) => (
          <label key={option.value} className="flex cursor-pointer items-center gap-2 text-sm">
            <input
              type="radio"
              name={controlId}
              value={option.value}
              disabled={disabled}
              checked={field.value === option.value}
              onChange={() => {
                onChange(field.id, option.value);
                onCommit?.();
              }}
            />
            {option.label}
          </label>
        ))}
      </div>
    );
  }
  if (field.kind === 'note') {
    return (
      <Textarea
        id={controlId}
        name={field.id}
        autoComplete={field.autoComplete}
        disabled={disabled}
        rows={3}
        value={field.value ?? ''}
        onChange={(event) => onChange(field.id, event.target.value)}
        onBlur={() => onCommit?.()}
        className={cn(CONTROL_CLASS_NAME, 'h-auto py-2')}
      />
    );
  }
  const inputType = field.kind === 'phone' ? 'tel' : field.kind;
  const inputMode = inputModeFor(field.kind);
  return (
    <Input
      id={controlId}
      name={field.id}
      autoComplete={autoCompleteFor(field)}
      spellCheck={field.kind === 'email' ? false : undefined}
      disabled={disabled}
      type={inputType}
      inputMode={inputMode}
      value={field.value ?? ''}
      onChange={(event) => onChange(field.id, event.target.value)}
      onBlur={() => onCommit?.()}
      className={CONTROL_CLASS_NAME}
    />
  );
}

export const SmartFormFields: FC<SmartFormFieldsProps> = ({
  fields,
  onChange,
  onCommit,
  columns = 2,
  prefilledLabel,
  disabled,
}) => {
  const prefix = useId();
  return (
    <div className="@container/form-fields w-full min-w-0">
      <div
        className="grid grid-cols-1 gap-x-4 gap-y-3.5 @xl/form-fields:grid-cols-2 @xl/form-fields:data-[columns=1]:grid-cols-1"
        data-columns={String(columns)}
      >
        {fields.map((field, index) => {
          const isWide = field.kind === 'note';
          const errors = field.errors ?? [];
          return (
            <div
              key={field.id}
              className="group animate-fadeUp only:col-span-full data-[wide=true]:col-span-full"
              data-wide={isWide}
              data-prefilled={field.prefilled === true}
              style={{ animationDelay: `${index * 70}ms` }}
            >
              <Label
                htmlFor={`${prefix}-${field.id}`}
                className="mb-1.5 block text-sm font-medium leading-normal text-foreground"
              >
                {field.label}
                {field.prefilled ? (
                  <span className="ml-1.5 text-xs font-normal text-muted-foreground-subtle">
                    {prefilledLabel}
                  </span>
                ) : null}
              </Label>
              {renderControl(field, onChange, onCommit, disabled, `${prefix}-${field.id}`)}
              {errors.map((error, errorIndex) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: error messages carry no stable id
                <div key={errorIndex} className="mx-0.5 mt-1 text-xs text-warning">
                  {error}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
};
